import React from 'react';
import classNames from 'classnames';
import s from './AiAnalyzingBorder.scss';

const RADIUS = 16;
const STROKE = 1.5;
const BLEED = 1;
const INSET = STROKE / 2 + BLEED;
const SEGMENT = 20;
const CARD_PHASE_OFFSETS = [0, 0.85, 1.55, 2.2, 2.95, 3.6];

const COMET_STOPS = [
  { offset: '0%', color: '#caebff' },
  { offset: '45%', color: '#7b91ff' },
  { offset: '100%', color: '#5532da' },
];

export type AiAnalyzingBorderMode = 'travel' | 'grow' | 'off';

interface AiAnalyzingBorderProps {
  mode: AiAnalyzingBorderMode;
  cardIndex: number;
}

export const AiAnalyzingBorder = ({
  mode,
  cardIndex,
}: AiAnalyzingBorderProps) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const travelRef = React.useRef<SVGRectElement>(null);
  const offsetRef = React.useRef('0');
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  const gradientId = React.useMemo(
    () => `ai-analyzing-comet-${cardIndex}`,
    [cardIndex],
  );

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        setSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (mode !== 'travel' || typeof requestAnimationFrame === 'undefined') {
      return undefined;
    }
    let raf = 0;
    const tick = () => {
      const rect = travelRef.current;
      if (rect) {
        offsetRef.current = getComputedStyle(rect).strokeDashoffset;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, size.width, size.height]);

  React.useLayoutEffect(() => {
    const rect = travelRef.current;
    if (!rect) {
      return;
    }
    rect.style.strokeDashoffset = mode === 'grow' ? offsetRef.current : '';
  }, [mode, size.width, size.height]);

  const { width, height } = size;
  const delay = -CARD_PHASE_OFFSETS[cardIndex % CARD_PHASE_OFFSETS.length];

  return (
    <div
      ref={rootRef}
      className={classNames(s.root, {
        [s.modeTravel]: mode === 'travel',
        [s.modeGrow]: mode === 'grow',
      })}
      data-hook="ai-analyzing-border"
      aria-hidden
    >
      {width > 0 && height > 0 && (
        <svg
          className={s.svg}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          fill="none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              {COMET_STOPS.map((stop) => (
                <stop
                  key={stop.offset}
                  offset={stop.offset}
                  stopColor={stop.color}
                />
              ))}
            </linearGradient>
          </defs>
          <rect
            ref={travelRef}
            className={s.travel}
            x={INSET}
            y={INSET}
            width={width - INSET * 2}
            height={height - INSET * 2}
            rx={RADIUS}
            pathLength={100}
            stroke={`url(#${gradientId})`}
            strokeDasharray={`${SEGMENT} ${100 - SEGMENT}`}
            style={{ animationDelay: `${delay}s` }}
          />
        </svg>
      )}
    </div>
  );
};
